import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Flex,
  Form,
  Grid,
  Input,
  List,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  BankOutlined,
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import {
  createCustomerManagement as createLine,
  deleteCustomerManagement as deleteLine,
  fetchAllGroupOrderOptions as fetchGroupOrderOptions,
  fetchCustomerManagement as fetchLines,
  updateCustomerManagement as updateLine,
} from "../api/customerManagementApi";
import {
  fetchCustomerByCodes,
  fetchCustomers,
  searchCustomers,
  searchCustomersByCompanyName,
} from "../../../entities/customer/api/customersApi";
import { splitRepresentativeId } from "../../../entities/customer/model/customerIdentity";
import { fetchCustomerLineOptions } from "../../../entities/line-user/api/lineUsersApi";
import type { CustomerResponse as Customer } from "../../../entities/customer/model/types";
import type {
  CustomerLineOption,
  GroupOrderOption,
  CustomerManagementRow as Line,
} from "../model/types";
import { getDisplayLabel } from "../../../shared/utils/getDisplayLabel";
import { MobilePagination } from "../../../shared/components/MobilePagination";
import { PageHeader } from "../../../shared/components/PageHeader";
import { formatApiTimestamp } from "../../../shared/utils/apiTimestamp";
import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import { appNotification } from "../../../shared/notifications/appNotification";
import { usePageRefresh } from "../../../shared/pull-refresh/PullRefreshContext";
import { LineForm } from "../components/CustomerManagementForm";
import "../styles/CustomerManagement.css";

const { useBreakpoint } = Grid;
const { Text } = Typography;

const customerKey = (customer: Customer) =>
  `${(customer.mt_code || "").trim()}::${(customer.branch_code || "").trim()}`;

const customerLabel = (customer: Customer) => {
  const mtCode = (customer.mt_code || "").trim();
  const branchCode = (customer.branch_code || "").trim();
  return branchCode ? `${mtCode} - ${branchCode}` : mtCode;
};

export const LineList = () => {
  const registerPageRefresh = usePageRefresh();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<Line | null>(null);
  const [customers, setCustomers] = useState<Record<string, Customer>>({});
  const [customerOptions, setCustomerOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [companyNameOptions, setCompanyNameOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [groupOrderOptions, setGroupOrderOptions] = useState<
    GroupOrderOption[]
  >([]);
  const [customerLineOptions, setCustomerLineOptions] = useState<
    CustomerLineOption[]
  >([]);
  const searchRef = useRef<NodeJS.Timeout | null>(null);
  const customerSearchRef = useRef<NodeJS.Timeout | null>(null);
  const companySearchRef = useRef<NodeJS.Timeout | null>(null);
  const formSessionRef = useRef(0);

  const cacheCustomers = useCallback((items: Customer[]) => {
    const nextCustomers: Record<string, Customer> = {};
    const nextCustomerOptions = new Map<
      string,
      { label: string; value: string }
    >();
    const nextCompanyOptions = new Map<
      string,
      { label: string; value: string }
    >();

    items.forEach((customer) => {
      const key = customerKey(customer);
      if (!customer.mt_code || !key) return;
      nextCustomers[key] = customer;
      nextCustomerOptions.set(key, {
        label: customerLabel(customer),
        value: key,
      });
      const companyName = getDisplayLabel(
        customer.company_out_name || customer.company_in_name,
      );
      if (companyName) {
        nextCompanyOptions.set(companyName, {
          label: companyName,
          value: companyName,
        });
      }
    });

    setCustomers((previous) => ({ ...previous, ...nextCustomers }));
    setCustomerOptions(Array.from(nextCustomerOptions.values()));
    setCompanyNameOptions(Array.from(nextCompanyOptions.values()));
  }, []);

  const loadCustomers = useCallback(
    async (
      keyword?: string,
      byCompanyName = false,
      expectedSession?: number,
    ) => {
      try {
        const result = keyword?.trim()
          ? byCompanyName
            ? await searchCustomersByCompanyName(keyword.trim())
            : await searchCustomers(keyword.trim())
          : await fetchCustomers();
        if (
          expectedSession !== undefined &&
          expectedSession !== formSessionRef.current
        )
          return;
        cacheCustomers(result.items || []);
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    },
    [cacheCustomers],
  );

  const loadLines = useCallback(
    async (keyword?: string) => {
      setLoading(true);
      try {
        const result = await fetchLines(
          page,
          pageSize,
          keyword !== undefined ? keyword : search,
          status,
        );
        setLines(result.items || []);
        setTotal(result.pagination.total || 0);
      } catch (error) {
        const apiError = normalizeApiError(error);
        appNotification.error({
          message: "お客様管理情報を取得できませんでした",
          description: apiError.message,
        });
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, search, status],
  );

  useEffect(() => {
    loadLines();
  }, [loadLines]);

  useEffect(() => {
    registerPageRefresh({ enabled: true, refresh: loadLines });
    return () => registerPageRefresh(null);
  }, [registerPageRefresh, loadLines]);

  const initialFormValues = useMemo(() => {
    if (!editingLine) return undefined;
    const customer = editingLine.customer;
    return {
      customer_key: customer ? customerKey(customer) : undefined,
      company_out_name:
        customer?.company_out_name || customer?.company_in_name || "",
      line_code: editingLine.line_id || editingLine.line_code,
      line_name: editingLine.line_name || undefined,
      group_id: editingLine.group_id,
      group_name: editingLine.group_name || undefined,
      staff_name:
        editingLine.status?.toUpperCase() === "PENDING"
          ? editingLine.line_name || undefined
          : editingLine.representative_name || undefined,
      phone_number: editingLine.phone_number || undefined,
    };
  }, [editingLine]);

  const handleCustomerChange = useCallback(
    (key?: string) => {
      if (!key) {
        form.setFieldValue("company_out_name", undefined);
        return;
      }
      const customer = customers[key];
      if (!customer) return;
      form.setFieldsValue({
        company_out_name: getDisplayLabel(
          customer.company_out_name || customer.company_in_name,
        ),
      });
    },
    [customers, form],
  );

  const handleCompanyNameChange = useCallback(
    (companyName?: string | number) => {
      if (!companyName) {
        form.setFieldValue("customer_key", undefined);
        return;
      }
      const matches = Object.values(customers).filter(
        (item) =>
          getDisplayLabel(item.company_out_name || item.company_in_name) ===
          companyName,
      );
      const options = new Map<string, { label: string; value: string }>();
      matches.forEach((customer) => {
        const key = customerKey(customer);
        options.set(key, { label: customerLabel(customer), value: key });
      });
      setCustomerOptions(Array.from(options.values()));
      if (matches[0])
        form.setFieldsValue({ customer_key: customerKey(matches[0]) });
    },
    [customers, form],
  );

  const handleGroupOrderChange = useCallback(
    async (groupId: string) => {
      const formSession = formSessionRef.current;
      const group = groupOrderOptions.find((item) => item.group_id === groupId);
      setCustomerOptions([]);
      setCompanyNameOptions([]);
      form.setFieldsValue({
        customer_key: undefined,
        company_out_name: undefined,
      });

      const pendingMember = group?.members?.find(
        (member) => member.status?.toUpperCase() === "PENDING",
      );
      if (pendingMember) {
        const lineOption = customerLineOptions.find(
          (item) => item.line_id === pendingMember.line_id,
        );
        const lineName =
          pendingMember.line_name?.trim() || lineOption?.line_name?.trim();
        form.setFieldsValue({
          line_code: pendingMember.line_id,
          line_name: lineName,
          staff_name: lineName,
          phone_number:
            pendingMember.phone_number?.trim() ||
            lineOption?.phone_number?.trim(),
        });
      }

      const representativeIdentity = splitRepresentativeId(
        group?.representative_id,
      );
      if (representativeIdentity.mt_code) {
        const unresolvedCustomer: Customer = {
          mt_code: representativeIdentity.mt_code,
          branch_code: representativeIdentity.branch_code,
          company_out_name: null,
          company_in_name: null,
        };
        cacheCustomers([unresolvedCustomer]);
        form.setFieldsValue({
          customer_key: customerKey(unresolvedCustomer),
          company_out_name: undefined,
        });

        try {
          const customer = await fetchCustomerByCodes(
            representativeIdentity.mt_code,
            representativeIdentity.branch_code,
          );
          if (formSession !== formSessionRef.current) return;
          cacheCustomers([customer]);
          form.setFieldsValue({
            customer_key: customerKey(customer),
            company_out_name: getDisplayLabel(
              customer.company_out_name || customer.company_in_name,
            ),
          });
        } catch (error) {
          if (formSession !== formSessionRef.current) return;
          console.error(
            "Error matching group representative ID in SHODAN:",
            error,
          );
        }
        return;
      }

      const sourceCompanyName = group?.company_name?.trim();
      if (!sourceCompanyName) {
        await loadCustomers(undefined, false, formSession);
        return;
      }

      try {
        const result = await searchCustomersByCompanyName(
          sourceCompanyName,
          true,
          1,
          100,
        );
        if (formSession !== formSessionRef.current) return;
        const suggestions = result.items || [];
        if (!suggestions.length) {
          await loadCustomers(undefined, false, formSession);
          return;
        }

        cacheCustomers(suggestions);
        const firstCustomer = suggestions[0];
        form.setFieldsValue({
          customer_key: customerKey(firstCustomer),
          company_out_name: getDisplayLabel(
            firstCustomer.company_out_name || firstCustomer.company_in_name,
          ),
        });
      } catch (error) {
        console.error("Error matching group company in SHODAN:", error);
        await loadCustomers(undefined, false, formSession);
      }
    },
    [
      cacheCustomers,
      customerLineOptions,
      form,
      groupOrderOptions,
      loadCustomers,
    ],
  );

  const clearFormState = useCallback(() => {
    formSessionRef.current += 1;
    form.resetFields();
    setCustomers({});
    setCustomerOptions([]);
    setCompanyNameOptions([]);
    setGroupOrderOptions([]);
    setCustomerLineOptions([]);
    if (customerSearchRef.current) clearTimeout(customerSearchRef.current);
    if (companySearchRef.current) clearTimeout(companySearchRef.current);
  }, [form]);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingLine(null);
    clearFormState();
  }, [clearFormState]);

  const handleOpenCustomerOptions = useCallback(() => {
    void loadCustomers();
  }, [loadCustomers]);

  const handleFormSubmit = async (values: any) => {
    setConfirmLoading(true);
    try {
      if (editingLine) {
        const customer = customers[values.customer_key] || editingLine.customer;
        await updateLine(editingLine.group_id!, editingLine.line_id!, {
          mt_code: customer?.mt_code?.trim(),
          branch_code: (customer?.branch_code || "").trim(),
          line_name: values.line_name,
          representative_name: values.staff_name,
          phone_number: values.phone_number,
        });
      } else {
        const customer = customers[values.customer_key];
        if (!customer?.mt_code)
          throw new Error("Customer selection is invalid");
        await createLine({
          group_id: values.group_id?.trim(),
          line_id: values.line_code?.trim(),
          line_name: values.line_name?.trim(),
          representative_name: values.staff_name?.trim(),
          phone_number: values.phone_number?.trim() || null,
          mt_code: customer.mt_code.trim(),
          branch_code: (customer.branch_code || "").trim(),
        });
      }

      appNotification.success({
        message: "保存完了",
        description: "お客様情報が正常に保存されました。",
      });
      handleCloseForm();
      await loadLines();
    } catch (error) {
      console.error("Error submitting customer management form:", error);
      throw error;
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => loadLines(value), 300);
  };

  const handleSearchCustomer = (value: string) => {
    if (customerSearchRef.current) clearTimeout(customerSearchRef.current);
    customerSearchRef.current = setTimeout(() => loadCustomers(value), 300);
  };

  const handleSearchCompanyName = (value: string) => {
    if (companySearchRef.current) clearTimeout(companySearchRef.current);
    companySearchRef.current = setTimeout(
      () => loadCustomers(value, true),
      300,
    );
  };

  const handleCreate = async () => {
    clearFormState();
    const formSession = formSessionRef.current;
    setEditingLine(null);
    setIsFormOpen(true);
    try {
      const [groups, customerLines, customerResult] = await Promise.all([
        fetchGroupOrderOptions(),
        fetchCustomerLineOptions(),
        fetchCustomers(1, 100),
      ]);
      if (formSession !== formSessionRef.current) return;
      setGroupOrderOptions(groups);
      setCustomerLineOptions(customerLines);
      cacheCustomers(customerResult.items || []);
    } catch (error) {
      console.error("Error loading create form options:", error);
      appNotification.error({
        message: "エラー",
        description: "グループまたはLINEユーザー情報の取得に失敗しました。",
      });
    }
  };

  const handleEdit = async (line: Line) => {
    clearFormState();
    const formSession = formSessionRef.current;
    setEditingLine(line);
    setIsFormOpen(true);
    const representativeIdentity = splitRepresentativeId(
      line.representative_id,
    );
    if (representativeIdentity.mt_code) {
      const unresolvedCustomer: Customer = {
        mt_code: representativeIdentity.mt_code,
        branch_code: representativeIdentity.branch_code,
        company_out_name: null,
        company_in_name: null,
      };
      let selectedCustomer = line.customer || unresolvedCustomer;
      if (!line.customer) {
        try {
          selectedCustomer = await fetchCustomerByCodes(
            representativeIdentity.mt_code,
            representativeIdentity.branch_code,
          );
        } catch (error) {
          console.error(
            "Error matching representative ID in SHODAN while editing:",
            error,
          );
        }
      }
      if (formSession !== formSessionRef.current) return;
      cacheCustomers([selectedCustomer]);
      form.setFieldsValue({
        customer_key: customerKey(selectedCustomer),
        company_out_name: getDisplayLabel(
          selectedCustomer.company_out_name || selectedCustomer.company_in_name,
        ),
      });
      return;
    }

    const sourceCompanyName = line.company_name?.trim();
    let suggestions: Customer[] = [];
    try {
      if (sourceCompanyName) {
        const result = await searchCustomersByCompanyName(
          sourceCompanyName,
          true,
          1,
          100,
        );
        suggestions = result.items || [];
      }
    } catch (error) {
      console.error("Error loading edit form company suggestions:", error);
    }

    try {
      if (formSession !== formSessionRef.current) return;
      const hasCompanySuggestions = suggestions.length > 0;
      if (!suggestions.length) {
        const result = await fetchCustomers(1, 100);
        suggestions = result.items || [];
      }
      if (formSession !== formSessionRef.current) return;

      const selectedCustomer =
        line.customer || (hasCompanySuggestions ? suggestions[0] : undefined);
      cacheCustomers([
        ...suggestions,
        ...(line.customer ? [line.customer] : []),
      ]);
      if (selectedCustomer) {
        form.setFieldsValue({
          customer_key: customerKey(selectedCustomer),
          company_out_name: getDisplayLabel(
            selectedCustomer.company_out_name ||
              selectedCustomer.company_in_name,
          ),
        });
      }
    } catch (error) {
      if (formSession !== formSessionRef.current) return;
      console.error("Error loading edit form customer options:", error);
      if (line.customer) cacheCustomers([line.customer]);
    }
  };

  const handleDelete = async (line: Line) => {
    try {
      await deleteLine(line.group_id!, line.line_id!);
      appNotification.success({
        message: "削除完了",
        description: "お客様情報が正常に削除されました。",
      });
      await loadLines();
    } catch (error) {
      appNotification.error({
        message: "エラー",
        description: "削除に失敗しました。",
      });
    }
  };

  const confirmDelete = (line: Line) => {
    Modal.confirm({
      title: "削除しますか?",
      content: "削除後は復元できず、一覧にも表示されません。",
      okText: "削除",
      cancelText: "キャンセル",
      okButtonProps: { danger: true },
      onOk: () => handleDelete(line),
    });
  };

  const statusTag = (value?: string) => (
    <Tag color={value === "SUCCESS" ? "green" : "orange"}>
      {value === "SUCCESS" ? "有効" : "処理待ち"}
    </Tag>
  );

  const columns: ColumnsType<Line> = [
    {
      title: "LINEユーザー名",
      dataIndex: "line_name",
      width: 130,
      fixed: screens.lg ? "left" : undefined,
      ellipsis: true,
    },
    {
      title: "LINEグループ名",
      dataIndex: "group_name",
      width: 150,
      // fixed: screens.lg ? "left" : undefined,
      ellipsis: true,
    },
    {
      title: "MTコード・枝番",
      ellipsis: {
        showTitle: true,
      },
      width: 130,
      render: (_, record) =>
        record.branch_code
          ? `${record.mt_code} - ${record.branch_code}`
          : record.mt_code || "--",
    },
    {
      title: "会社名",
      width: 160,
      ellipsis: true,
      render: (_, record) => record.customer?.company_out_name || "--",
    },
    {
      title: "ご担当者名",
      width: 120,
      ellipsis: true,
      render: (_, record) =>
        record.representative_name || record.line_name || "--",
    },
    // {
    //   title: "電話番号",
    //   dataIndex: "phone_number",
    //   width: 120,
    //   ellipsis: true,
    // },
    {
      title: "ステータス",
      ellipsis: {
        showTitle: true,
      },
      dataIndex: "status",
      width: 100,
      align: "center",
      render: statusTag,
    },
    {
      title: "作成日",
      ellipsis: {
        showTitle: true,
      },
      dataIndex: "assigned_at",
      width: 105,
      // fixed: screens.lg ? "right" : undefined,
      render: (date?: string) => formatApiTimestamp(date),
    },
    {
      title: "操作",
      key: "actions",
      width: 110,
      fixed: screens.lg ? "right" : undefined,
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="編集">
            <Button
              type="primary"
              style={{ borderRadius: 50 }}
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Button
            danger
            style={{ borderRadius: 50 }}
            icon={<DeleteOutlined />}
            onClick={() => confirmDelete(record)}
          />
        </Space>
      ),
    },
  ];

  const pageHeader = (
    <PageHeader
      title="顧客管理"
      description="お客様のLINEアカウント一覧"
      actionLabel="新規作成"
      onAction={handleCreate}
    />
  );

  const toolbar = (
    <Flex
      className="customer-list-header"
      justify="space-between"
      align="center"
      wrap="wrap"
      gap={12}
    >
      <Input
        className="customer-list-search"
        allowClear
        size="large"
        placeholder="検索する情報を入力してください..."
        prefix={<SearchOutlined />}
        value={search}
        onChange={(event) => handleSearch(event.target.value)}
        style={{ width: 350 }}
      />
      <Flex className="customer-list-actions" gap={12} wrap="wrap">
        <Select
          allowClear
          size="large"
          placeholder="ステータス"
          style={{ width: 180 }}
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
          options={[
            { label: "有効", value: "success" },
            { label: "処理待ち", value: "pending" },
          ]}
        />
        {!isMobile && (
          <Button
            size="large"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => {
              setSearch("");
              setStatus(undefined);
              setPage(1);
              loadLines("");
            }}
          >
            更新
          </Button>
        )}
      </Flex>
    </Flex>
  );

  const formModal = (
    <LineForm
      open={isFormOpen}
      loading={confirmLoading}
      form={form}
      customerOptions={customerOptions}
      companyNameOptions={companyNameOptions}
      groupOrderOptions={groupOrderOptions}
      customerLineOptions={customerLineOptions}
      onSearchCustomer={handleSearchCustomer}
      onSearchCompanyName={handleSearchCompanyName}
      onCustomerChange={handleCustomerChange}
      onCompanyNameChange={handleCompanyNameChange}
      onGroupChange={handleGroupOrderChange}
      onOpenCustomerOptions={handleOpenCustomerOptions}
      initialValues={initialFormValues}
      onClose={handleCloseForm}
      onSubmit={handleFormSubmit}
    />
  );

  if (isMobile) {
    return (
      <>
        <div className="customer-management-page">
          {pageHeader}
          <Card className="customer-list-card" bordered={false}>
            {toolbar}
            <List
              className="customer-mobile-list"
              loading={loading}
              dataSource={lines}
              pagination={false}
              renderItem={(line) => (
                <List.Item className="customer-mobile-list-item">
                  <Card className="customer-mobile-card" size="small">
                    <div className="customer-mobile-card-content">
                      <div className="customer-mobile-card-header">
                        <Text strong className="customer-mobile-card-title">
                          {line.representative_name || line.line_name || "--"}
                        </Text>
                        {statusTag(line.status)}
                      </div>
                      <div className="customer-mobile-card-details">
                        <Text className="customer-mobile-card-code">
                          {line.branch_code
                            ? `${line.mt_code} - ${line.branch_code}`
                            : line.mt_code || "--"}
                        </Text>
                        <div className="customer-mobile-card-detail">
                          <BankOutlined
                            className="customer-mobile-card-detail-icon"
                          />
                          <div className="customer-mobile-card-detail-content">
                            <Text className="customer-mobile-card-detail-label">
                              会社名
                            </Text>
                            <Text className="customer-mobile-card-detail-value">
                              {line.customer?.company_out_name || "--"}
                            </Text>
                          </div>
                        </div>
                        <div className="customer-mobile-card-detail">
                          <MessageOutlined
                            className="customer-mobile-card-detail-icon"
                          />
                          <div className="customer-mobile-card-detail-content">
                            <Text className="customer-mobile-card-detail-label">
                              LINEユーザー名
                            </Text>
                            <Text className="customer-mobile-card-detail-value">
                              {line.line_name || "--"}
                            </Text>
                          </div>
                        </div>
                        <div className="customer-mobile-card-detail">
                          <TeamOutlined
                            className="customer-mobile-card-detail-icon"
                          />
                          <div className="customer-mobile-card-detail-content">
                            <Text className="customer-mobile-card-detail-label">
                              LINEグループ名
                            </Text>
                            <Text className="customer-mobile-card-detail-value">
                              {line.group_name || "--"}
                            </Text>
                          </div>
                        </div>
                      </div>
                      <div className="customer-mobile-card-actions">
                        <Button
                          type="primary"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(line)}
                        >
                          編集
                        </Button>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => confirmDelete(line)}
                        >
                          削除
                        </Button>
                      </div>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
            <MobilePagination
              current={page}
              pageSize={pageSize}
              total={total}
              onChange={setPage}
            />
          </Card>
        </div>
        {formModal}
      </>
    );
  }

  return (
    <div className="customer-management-page">
      {pageHeader}
      <Card className="customer-list-card" bordered={false}>
        {toolbar}
        <Table
          className="customer-list-table"
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={lines}
          scroll={{ x: 800 }}
          tableLayout="fixed"
          pagination={{
            current: page,
            pageSize,
            total,
            size: "middle",
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPageSize !== pageSize ? 1 : nextPage);
              setPageSize(nextPageSize);
            },
            showTotal: (count, range) =>
              `${count} 件中 ${range[0]}-${range[1]} を表示`,
          }}
        />
      </Card>
      {formModal}
    </div>
  );
};
